/**
 * 热点雷达 · 顶部多维筛选条
 * - 题材(6) · 频道(男频/女频) · 排序 · 已屏蔽 toggle
 */
import { useState } from 'react';
import { X, Filter, ChevronDown, EyeOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  GENRES,
  AUDIENCES,
  RADAR_SORTS,
  RADAR_SORT_LABELS,
  type Genre,
  type Audience,
  type RadarSort,
} from '@starfox/shared-types';

export interface RadarFilters {
  genres: Genre[];
  audiences: Audience[];
  sort: RadarSort;
  includeBanned: boolean;
}

export const DEFAULT_FILTERS: RadarFilters = {
  genres: [],
  audiences: [],
  sort: 'suit',
  includeBanned: false,
};

interface Props {
  value: RadarFilters;
  onChange: (next: RadarFilters) => void;
  counts?: { topics: number; banned: number };
}

export function FilterBar({ value, onChange, counts }: Props) {
  const [openGenre, setOpenGenre] = useState(false);

  const toggleGenre = (g: Genre) => {
    const next = value.genres.includes(g)
      ? value.genres.filter((x) => x !== g)
      : [...value.genres, g];
    onChange({ ...value, genres: next });
  };
  const toggleAud = (a: Audience) => {
    const next = value.audiences.includes(a)
      ? value.audiences.filter((x) => x !== a)
      : [...value.audiences, a];
    onChange({ ...value, audiences: next });
  };
  const clear = () => onChange(DEFAULT_FILTERS);
  const activeCount = value.genres.length + value.audiences.length;

  return (
    <div className="space-y-3 rounded-lg border border-outline-variant bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-neutral-500" />
        <span className="text-sm font-medium text-neutral-700">筛选</span>

        {/* 题材下拉 */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpenGenre((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border border-outline-variant bg-surface px-3 py-1.5 text-sm transition',
              openGenre ? 'border-brand-500 ring-2 ring-brand-500/20' : 'hover:bg-surface-variant',
            )}
          >
            题材
            {value.genres.length > 0 && (
              <span className="rounded-full bg-brand-500 px-1.5 text-xs text-white">
                {value.genres.length}
              </span>
            )}
            <ChevronDown className={cn('h-3.5 w-3.5 transition', openGenre && 'rotate-180')} />
          </button>
          {openGenre && (
            <Dropdown onClose={() => setOpenGenre(false)}>
              <div className="grid grid-cols-2 gap-1.5">
                {GENRES.map((g) => (
                  <Chip key={g} active={value.genres.includes(g)} onClick={() => toggleGenre(g)}>
                    {g}
                  </Chip>
                ))}
              </div>
            </Dropdown>
          )}
        </div>

        {/* 频道：男频 / 女频 toggle */}
        <div className="inline-flex rounded-lg border border-outline-variant p-0.5">
          {AUDIENCES.map((a) => (
            <button
              type="button"
              key={a}
              onClick={() => toggleAud(a)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs transition',
                value.audiences.includes(a)
                  ? 'bg-brand-500 text-white'
                  : 'text-neutral-600 hover:bg-surface-variant',
              )}
            >
              {a}
            </button>
          ))}
        </div>

        {/* 排序 */}
        <div className="inline-flex rounded-lg border border-outline-variant p-0.5">
          {RADAR_SORTS.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => onChange({ ...value, sort: s })}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs transition',
                value.sort === s
                  ? 'bg-brand-500 text-white'
                  : 'text-neutral-600 hover:bg-surface-variant',
              )}
            >
              {RADAR_SORT_LABELS[s]}
            </button>
          ))}
        </div>

        {/* 已屏蔽 toggle */}
        <button
          type="button"
          onClick={() => onChange({ ...value, includeBanned: !value.includeBanned })}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs transition',
            value.includeBanned
              ? 'border-warning bg-warning-soft text-warning'
              : 'border-outline-variant text-neutral-500 hover:bg-surface-variant',
          )}
          title="加载已屏蔽题目以便审阅"
        >
          <EyeOff className="h-3.5 w-3.5" />
          含已屏蔽
        </button>

        <div className="ml-auto flex items-center gap-2 text-xs text-neutral-500">
          {counts && (
            <span>
              主榜 <strong className="text-neutral-700">{counts.topics}</strong>
              {value.includeBanned && (
                <>
                  {' · '}
                  屏蔽 <strong className="text-warning">{counts.banned}</strong>
                </>
              )}
            </span>
          )}
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clear}>
              <X className="h-3.5 w-3.5" /> 清空
            </Button>
          )}
        </div>
      </div>

      {/* 已选 chips */}
      {(value.genres.length > 0 || value.audiences.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {value.genres.map((g) => (
            <Badge key={g} variant="info" className="cursor-pointer" onClick={() => toggleGenre(g)}>
              {g} <X className="h-3 w-3" />
            </Badge>
          ))}
          {value.audiences.map((a) => (
            <Badge
              key={a}
              variant="success"
              className="cursor-pointer"
              onClick={() => toggleAud(a)}
            >
              {a} <X className="h-3 w-3" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function Dropdown({
  children,
  onClose,
  className,
}: {
  children: React.ReactNode;
  onClose: () => void;
  className?: string;
}) {
  return (
    <>
      <button
        type="button"
        aria-label="close"
        className="fixed inset-0 z-10 cursor-default"
        onClick={onClose}
      />
      <div
        className={cn(
          'absolute left-0 top-full z-20 mt-2 min-w-[24rem] max-w-[36rem] rounded-lg border border-outline-variant bg-surface p-3 shadow-m2',
          className,
        )}
      >
        {children}
      </div>
    </>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-2.5 py-1 text-left text-xs transition',
        active
          ? 'border-brand-500 bg-brand-500/10 text-brand-500'
          : 'border-outline-variant text-neutral-700 hover:bg-surface-variant',
      )}
    >
      {children}
    </button>
  );
}
